import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Button, Flex, Table, Tooltip, message, Spin } from 'antd'
import type { TableProps } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import {
  EditOutlined,
  DeleteOutlined,
  BarChartOutlined,
  CopyOutlined,
} from '@ant-design/icons'

import Title from 'antd/es/typography/Title'
import type { LeadFlow } from '@/data'
import { getFlows } from '@/api/lop/lead-flows/getFlows'
import { deleteFlow } from '@/api/lop/lead-flows/deleteFlow'
import { getFlow } from '@/api/lop/lead-flows/getFlow'
import { createFlow } from '@/api/lop/lead-flows/createFlow'
import { getFunnelsURL } from '@/api/client'
import { AuthContext } from '@/contexts/AuthContext'
import { hasPermission } from '@/auth/access'
import { resolveBackendSort } from '@/utils/backendSort'
import { SearchInput } from '@/components/SearchInput'
import { getErrorMessage } from '@/utils/getErrorMessage'

const templateLabels: Record<string, string> = {
  'savings-estimates': 'Savings Estimates',
  'finding-assistance': 'Finding Assistance',
  'getting-started': 'Getting Started',
  chat: 'Chat',
}

export const LeadFlowList: React.FC = () => {
  const navigate = useNavigate()
  const auth = useContext(AuthContext)!
  const [flows, setFlows] = useState<LeadFlow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  })
  const [sortField, setSortField] = useState('-metrics.visitor_count')
  const [templateFilter, setTemplateFilter] = useState<string>()
  const [search, setSearch] = useState('')

  const hasFetched = useRef(false)
  const canReadLeadFlows = hasPermission(
    auth.user,
    'lop:ui:action:read:lead-flows',
  )
  const canCreateLeadFlows = hasPermission(
    auth.user,
    'lop:ui:action:create:lead-flows',
  )
  const canUpdateLeadFlows = hasPermission(
    auth.user,
    'lop:ui:action:update:lead-flows',
  )
  const canDeleteLeadFlows = hasPermission(
    auth.user,
    'lop:ui:action:delete:lead-flows',
  )

  const fetchFlows = useCallback(
    async (
      page = 1,
      size = pagination.pageSize,
      sort = sortField,
      template = templateFilter,
      query = search,
    ) => {
      try {
        setLoading(true)
        setError(null)
        const filters: { key: string; value: string }[] = []
        if (template) {
          filters.push({ key: 'template_slug', value: template })
        }
        if (query) {
          filters.push({ key: 'flow', value: query })
        }
        const { data, meta } = await getFlows({
          page,
          per_page: size,
          include: 'metrics',
          sort,
          ...(filters.length ? { filters } : {}),
        })
        setFlows(data)
        setPagination({
          current: meta.current_page,
          pageSize: meta.per_page,
          total: meta.total,
        })
      } catch (err) {
        setError('Failed to load lead flows')
        console.error(err)
      } finally {
        setLoading(false)
      }
    },
    [pagination.pageSize, sortField, templateFilter, search],
  )

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value)
      fetchFlows(1, pagination.pageSize, sortField, templateFilter, value)
    },
    [fetchFlows, pagination.pageSize, sortField, templateFilter],
  )

  useEffect(() => {
    if (hasFetched.current) return
    if (!canReadLeadFlows) return
    hasFetched.current = true
    fetchFlows()
  }, [canReadLeadFlows, fetchFlows])

  const handleDelete = async (slug: string) => {
    try {
      await deleteFlow(slug)
      message.success('Flow deleted')
      fetchFlows(
        pagination.current,
        pagination.pageSize,
        sortField,
        templateFilter,
      )
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to delete flow'))
      console.error(err)
    }
  }

  const handleCopy = async (slug: string) => {
    try {
      const flow = await getFlow(slug)
      if (!flow) {
        message.error('Flow not found')
        return
      }
      await createFlow({
        name: `(Duplicate) ${flow.name}`,
        slug: `${flow.slug}-copy`,
        template_slug: flow.template_slug,
        steps: flow.steps.map((step) => ({
          ordinal: step.ordinal,
          headline: step.headline,
          sub_headline: step.sub_headline,
          cta_button: step.cta_button,
          inputs: step.inputs,
        })),
        chat_id: flow.chat_id,
        chat_query: flow.chat_query ?? null,
        pixel_ids: flow.pixels?.map((p) => p.id) ?? [],
        downsell_id: flow.downsell_id,
      })
      message.success('Flow duplicated')
      fetchFlows(
        pagination.current,
        pagination.pageSize,
        sortField,
        templateFilter,
      )
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to duplicate flow'))
    }
  }

  const columnSortKeys = {
    name: 'name',
    lead_count: 'metrics.lead_count',
    // cpa: 'metrics.cpa', use cvr instead for now until cpa value is accurate
    cvr: 'metrics.cvr',
    created_at: 'id',
  } as const

  const columns: TableProps<LeadFlow>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text, record) => (
        <a
          href={`${getFunnelsURL()}/t/${record.template_slug}/?flow=${record.slug}&skiptesting=true`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {text}
        </a>
      ),
    },
    {
      title: 'Template',
      dataIndex: 'template_slug',
      key: 'template_slug',
      sorter: false,
      filters: Object.entries(templateLabels).map(([slug, label]) => ({
        text: label,
        value: slug,
      })),
      filterMultiple: false,
      render: (slug: string) => templateLabels[slug] || slug,
    },
    {
      title: 'Visitors',
      key: 'lead_count',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (_, record) =>
        record.metrics?.visitor_count != null
          ? record.metrics.visitor_count.toLocaleString()
          : '—',
    },
    // {
    //   title: 'CPA',
    //   key: 'cpa',
    //   sorter: true,
    //   render: (_, record) =>
    //     record.metrics?.cpa != null ? `${record.metrics.cpa}%` : '—',
    // },
    {
      title: 'CVR',
      key: 'cvr',
      sorter: true,
      render: (_, record) =>
        record.metrics?.cvr != null ? `${record.metrics.cvr}%` : '—',
    },
    {
      title: 'Creation Date',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      render: (value: string) =>
        value
          ? new Date(value).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Flex gap="middle">
          {canUpdateLeadFlows ? (
            <Tooltip title="Edit">
              <Link to={`/lead-flows/${record.slug}/edit/form-info`}>
                <EditOutlined style={{ fontSize: 16 }} />
              </Link>
            </Tooltip>
          ) : null}
          {canCreateLeadFlows ? (
            <Tooltip title="Duplicate">
              <CopyOutlined
                style={{ fontSize: 16, cursor: 'pointer' }}
                onClick={() => handleCopy(record.slug)}
              />
            </Tooltip>
          ) : null}
          <Tooltip title="Reports">
            <BarChartOutlined style={{ fontSize: 16, cursor: 'pointer' }} />
          </Tooltip>
          {canDeleteLeadFlows ? (
            <Tooltip title="Delete">
              <DeleteOutlined
                style={{ fontSize: 16, color: '#ff4d4f', cursor: 'pointer' }}
                onClick={() => handleDelete(record.slug)}
              />
            </Tooltip>
          ) : null}
        </Flex>
      ),
    },
  ]

  if (error) {
    return (
      <div>
        <Title level={4}>Lead Flows</Title>
        <p style={{ color: '#ff4d4f' }}>{error}</p>
        <Button onClick={() => fetchFlows()}>Retry</Button>
      </div>
    )
  }

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Lead Flows
        </Title>
        <Flex gap="small" align="center">
          <SearchInput onSearch={handleSearch} />
          {canCreateLeadFlows ? (
            <Button type="primary" onClick={() => navigate('/lead-flows/new')}>
              New
            </Button>
          ) : null}
        </Flex>
      </Flex>
      {canReadLeadFlows ? (
        <Spin spinning={loading}>
          <Table<LeadFlow>
            rowKey="id"
            columns={columns}
            dataSource={flows}
            pagination={pagination}
            onChange={(p, filters, sorter) => {
              const sort = resolveBackendSort(
                sorter,
                columnSortKeys,
                '-metrics.visitor_count',
              )
              const template = filters.template_slug?.[0] as string | undefined
              setSortField(sort)
              setTemplateFilter(template)
              fetchFlows(p.current, p.pageSize, sort, template, search)
            }}
          />
        </Spin>
      ) : null}
    </div>
  )
}
